import React, { useState } from 'react';
import { FileText, Plus, Search, Filter, Edit, Trash2, X } from 'lucide-react';
import { contractCategories } from '../../data/contracts';
import { useContracts } from '../../context/ContractContext';
import { toast } from 'react-hot-toast';

const ContractManagement = () => {
  const { contracts, updateContract, deleteContract } = useContracts();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentContract, setCurrentContract] = useState<any>(null);

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory ? contract.category === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });

  const handleEditContract = (contract: any) => {
    setCurrentContract({
      ...contract,
      keyPoints: [...contract.keyPoints],
      useCases: [...contract.useCases],
      relatedDocuments: [...contract.relatedDocuments]
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteContract = (contractId: string) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      deleteContract(contractId);
      toast.success('Contract deleted successfully');
    }
  };

  const handleSaveContract = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentContract) return;

    // Validate the contract data
    if (!currentContract.name || !currentContract.category || !currentContract.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Ensure price values are valid
    if (currentContract.price.min < 0 || currentContract.price.max < currentContract.price.min) {
      toast.error('Please enter valid price values');
      return;
    }

    updateContract(currentContract);
    setIsEditModalOpen(false);
  };

  const handleAddListItem = (field: 'keyPoints' | 'useCases' | 'relatedDocuments') => {
    if (!currentContract) return;
    setCurrentContract({
      ...currentContract,
      [field]: [...currentContract[field], '']
    });
  };

  const handleRemoveListItem = (field: 'keyPoints' | 'useCases' | 'relatedDocuments', index: number) => {
    if (!currentContract) return;
    const newItems = [...currentContract[field]];
    newItems.splice(index, 1);
    setCurrentContract({
      ...currentContract,
      [field]: newItems
    });
  };

  const handleListItemChange = (field: 'keyPoints' | 'useCases' | 'relatedDocuments', index: number, value: string) => {
    if (!currentContract) return;
    const newItems = [...currentContract[field]];
    newItems[index] = value;
    setCurrentContract({
      ...currentContract,
      [field]: newItems
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Contract Management</h1>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <Plus className="w-5 h-5" />
          New Contract
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search contracts..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {contractCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price Range</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processing Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContracts.map(contract => {
                const category = contractCategories.find(cat => cat.id === contract.category);
                return (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{contract.name}</span>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{contract.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {category?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {contract.price.min === contract.price.max 
                        ? `${contract.price.min} €`
                        : `${contract.price.min} - ${contract.price.max} €`
                      }
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{contract.processingTime}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex gap-2">
                        <button 
                          className="text-blue-600 hover:text-blue-800"
                          onClick={() => handleEditContract(contract)}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button 
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDeleteContract(contract.id)}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && currentContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Edit Contract</h2>
                <button 
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSaveContract}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={currentContract.name}
                      onChange={(e) => setCurrentContract({...currentContract, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={currentContract.category}
                      onChange={(e) => setCurrentContract({...currentContract, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {contractCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={currentContract.description}
                      onChange={(e) => setCurrentContract({...currentContract, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Price (€)</label>
                      <input
                        type="number"
                        value={currentContract.price.min}
                        onChange={(e) => setCurrentContract({
                          ...currentContract,
                          price: {...currentContract.price, min: Number(e.target.value)}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Price (€)</label>
                      <input
                        type="number"
                        value={currentContract.price.max}
                        onChange={(e) => setCurrentContract({
                          ...currentContract,
                          price: {...currentContract.price, max: Number(e.target.value)}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Processing Time</label>
                    <input
                      type="text"
                      value={currentContract.processingTime}
                      onChange={(e) => setCurrentContract({...currentContract, processingTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Key Points */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Key Points</label>
                      <button
                        type="button"
                        onClick={() => handleAddListItem('keyPoints')}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        + Add Key Point
                      </button>
                    </div>
                    {currentContract.keyPoints.map((point: string, index: number) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={point}
                          onChange={(e) => handleListItemChange('keyPoints', index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveListItem('keyPoints', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Use Cases */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Use Cases</label>
                      <button
                        type="button"
                        onClick={() => handleAddListItem('useCases')}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        + Add Use Case
                      </button>
                    </div>
                    {currentContract.useCases.map((useCase: string, index: number) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={useCase}
                          onChange={(e) => handleListItemChange('useCases', index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveListItem('useCases', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Related Documents */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Related Documents</label>
                      <button
                        type="button"
                        onClick={() => handleAddListItem('relatedDocuments')}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        + Add Document
                      </button>
                    </div>
                    {currentContract.relatedDocuments.map((doc: string, index: number) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={doc}
                          onChange={(e) => handleListItemChange('relatedDocuments', index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveListItem('relatedDocuments', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractManagement;